// lib/http.ts
import axios, { AxiosError, type AxiosInstance } from 'axios';
import { env } from '@/shared/config';

import {
  API_BASE_URL,
  CORE_BASE_URL,
  authHeader,
  expireSession,
  getAccessToken,
  isPublicAuthPage,
  refreshSession,
} from './session';

/** Пометка на запросе, что его уже один раз повторяли после обновления токена. */
type RetriableConfig = { _retriedAfterRefresh?: boolean };

/**
 * Общая обвязка для обоих бинарей бэка.
 *
 * Личность отправляется двумя способами сразу:
 *  - `Authorization: Bearer` — нативная сессия (единственный рабочий способ,
 *    когда фронт и бэк на разных origin, см. shared/api/session.ts);
 *  - кука `jwt_token` через `withCredentials` — старая сессия Bitrix24.
 *
 * HttpOnly-куку самой v2 (`doit_jwt`) браузер отправит тоже, но при Bearer бэк
 * её не смотрит — опираться на неё не нужно (см. shared/api/session.ts).
 *
 * На 401 сначала пробуем обновить пару токенов и повторить запрос один раз.
 * Если обновление не удалось — сессия кончилась, разлогиниваем приложение.
 */
export function createHttp(baseURL: string): AxiosInstance {
  const instance = axios.create({ baseURL, withCredentials: true });

  instance.interceptors.request.use((config) => {
    const header = authHeader();
    if (header) {
      config.headers.set('Authorization', header.Authorization);
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const status = error.response?.status;
      const config = error.config as
        | (typeof error.config & RetriableConfig)
        | undefined;

      if (status !== 401 || env.authBypass) {
        return Promise.reject(error);
      }

      // На страницах входа и регистрации 401 — это «неверные данные»,
      // а не протухшая сессия.
      if (isPublicAuthPage()) {
        return Promise.reject(error);
      }

      // Нативной сессии нет вовсе — вести себя как раньше: разлогинить.
      if (!getAccessToken()) {
        expireSession();
        return Promise.reject(error);
      }

      if (!config) {
        expireSession();
        return Promise.reject(error);
      }

      // Повтор с только что обновлённым токеном снова получил 401 — значит
      // токен рабочий, а конкретная ручка не пускает (у бэка есть маршруты,
      // отвечающие 401 вместо 403). Разлогинивать за это нельзя: человек
      // терял сессию из-за одной запрещённой кнопки.
      if (config._retriedAfterRefresh) {
        return Promise.reject(error);
      }

      const token = await refreshSession();
      if (!token) {
        return Promise.reject(error);
      }

      config._retriedAfterRefresh = true;
      config.headers?.set?.('Authorization', `Bearer ${token}`);

      return instance.request(config);
    }
  );

  return instance;
}

/** Основной бинарь: `/v2/tasks*`, `/v2/groups*` и legacy-маршруты Bitrix. */
export const http = createHttp(API_BASE_URL);

/** Бинарь core/identity: `/auth/*` и `/v2/users*`. */
export const coreHttp = createHttp(CORE_BASE_URL);
